import { Select } from 'antd'
import { FunctionComponent, ReactElement, useState } from 'react'
import styled from 'styled-components'
import ViewSwitcher, {
  ViewMode,
} from '@/components/common/buttons/view-switcher'
import QuickSearchKolInput from '@/components/common/quick-search-kol-input'
import PlatformSelector, {
  StyledPlatformSelector,
} from '@/components/common/selectors/platform-selector'
import { Icon } from '@/components/custom-icon'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  FetchPostCollectionPostListRequest,
  FetchPostCollectionPostSortBy,
} from '@/types/api/post-collection'
import { ampli } from '@/utils/ampli'
import { PlatformShortcode } from '@/utils/convert-platform'

const sortConfigs: {
  value: FetchPostCollectionPostSortBy
  i18nId: I18nId
}[] = [
  {
    value: FetchPostCollectionPostSortBy.CreatedAtDesc,
    i18nId: 'general:default',
  },
  {
    value: FetchPostCollectionPostSortBy.PublishedAtDesc,
    i18nId: 'kol:content_sort_option_updated',
  },
  {
    value: FetchPostCollectionPostSortBy.EngagementCountDesc,
    i18nId: 'kol:post_option_engagement',
  },
  {
    value: FetchPostCollectionPostSortBy.WatchCountDesc,
    i18nId: 'kol:post_option_view',
  },
  {
    value: FetchPostCollectionPostSortBy.LikeCountDesc,
    i18nId: 'kol:engagements_chart_illu_like',
  },
  {
    value: FetchPostCollectionPostSortBy.CommentCountDesc,
    i18nId: 'kol:engagements_chart_illu_comment',
  },
]

export type PostListFilter = Pick<
  FetchPostCollectionPostListRequest,
  'platform' | 'kol_uuid' | 'sort_by'
> & {
  kolName?: string
}

export const postCollectionAvailablePlatforms = [
  PlatformShortcode.Instagram,
  PlatformShortcode.Facebook,
  PlatformShortcode.YouTube,
  PlatformShortcode.TikTok,
  PlatformShortcode.Twitter,
  PlatformShortcode.Threads,
]

interface PostListHeaderProps {
  viewMode: ViewMode
  filtersState: PostListFilter
  onFiltersChange: (filters: PostListFilter) => void
  onViewChange: (view: ViewMode) => void
}
const PostListHeader: FunctionComponent<PostListHeaderProps> = ({
  filtersState,
  viewMode,
  onFiltersChange,
  onViewChange,
}) => {
  const { formatMessage } = useIntl()
  const [searchName, setSearchName] = useState<string>('')

  return (
    <Wrapper>
      <SearchFilters>
        <PlatformSelector
          hasAllOption
          showIcon
          availablePlatforms={postCollectionAvailablePlatforms}
          value={filtersState.platform}
          onChange={(platform: PlatformShortcode): void => {
            ampli.changeCollectionFilter({
              type: 'platform',
              prev: filtersState.platform ?? PlatformShortcode.All,
              next: platform,
              collectionType: 'Post',
            })

            onFiltersChange({ ...filtersState, platform })
          }}
        />
        <QuickSearchKolInput
          searchName={searchName}
          onSearchNameChange={setSearchName}
          onSelectKol={(kol, kolName): void => {
            const kol_uuid = kol?.uuid
            onFiltersChange({ ...filtersState, kol_uuid, kolName })
          }}
        />
      </SearchFilters>
      <SwitcherFilters>
        <SortSwitcher>
          <Select
            defaultValue={formatMessage({ id: sortConfigs[0].i18nId })}
            dropdownRender={(menu): ReactElement => (
              <DropdownWrapper>{menu}</DropdownWrapper>
            )}
            popupMatchSelectWidth={false}
            onChange={(value: FetchPostCollectionPostSortBy): void =>
              onFiltersChange({ ...filtersState, sort_by: value })
            }
          >
            {sortConfigs.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                <Icon style={{ marginRight: '4px' }} type='elIconSort' />
                {formatMessage({ id: item.i18nId })}
              </Select.Option>
            ))}
          </Select>
        </SortSwitcher>
        <ViewSwitcher viewMode={viewMode} onViewChange={onViewChange} />
      </SwitcherFilters>
    </Wrapper>
  )
}

const SearchFilters = styled.div`
  display: flex;
  align-items: center;
`

const SwitcherFilters = styled.div`
  width: fit-content;
  align-items: center;
  display: flex;
  flex-wrap: wrap;

  @media (max-width: 991px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const SortSwitcher = styled.div`
  .ant-select > {
    .ant-select-selector {
      border-radius: 4px;
      height: 36px;
      width: 200px;
      border: 1px solid ${({ theme }): string => theme.colors.border.border};

      .ant-select-selection-item {
        line-height: 36px;
      }
    }
  }
`

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  ${StyledPlatformSelector} {
    margin-right: 16px;
  }

  ${SortSwitcher} {
    margin-right: 24px;
  }

  @media (max-width: 1140px) {
    flex-direction: column;
    ${SwitcherFilters} {
      margin-top: 6px;
    }
  }
`

const DropdownWrapper = styled.div`
  .anticon {
    display: none;
  }
`

export default PostListHeader
